import { useCallback, useSyncExternalStore } from 'react';

/**
 * Tracks whether an embedded content type, such as Slides, has supplied
 * `orderedActiveAnnotationIdList` for the current content.
 *
 * Pages fill that list while rendering, so it is complete as soon as the page appears.
 * Content types rendered in an iframe send it over the bridge after the iframe loads.
 * Before that, the list is empty because it has not arrived yet, not because the content
 * has no comments, so the comments panel must not classify every thread as dangling.
 */

// Only one content is mounted at a time, so the most recent content ID is enough.
let receivedForContentId: string | undefined;
const subscribers = new Set<() => void>();

const notifySubscribers = (): void => {
	subscribers.forEach((callback) => {
		callback();
	});
};

const subscribe = (callback: () => void): (() => void) => {
	subscribers.add(callback);
	return () => {
		subscribers.delete(callback);
	};
};

/**
 * Records that `orderedActiveAnnotationIdList` has been supplied for `contentId`. Call
 * it after `setOrderedActiveAnnotationIdList`, so the list is already stored when
 * subscribers re-render.
 */
export const markActiveCommentMarkersReceived = (contentId: string): void => {
	if (receivedForContentId === contentId) {
		return;
	}
	receivedForContentId = contentId;
	notifySubscribers();
};

/**
 * Whether `orderedActiveAnnotationIdList` has been supplied for `contentId`. Returns
 * `false` for an `undefined` content ID.
 */
export const useHasReceivedActiveCommentMarkers = (contentId: string | undefined): boolean => {
	const getSnapshot = useCallback(
		() => contentId !== undefined && receivedForContentId === contentId,
		[contentId],
	);
	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export const clearActiveCommentMarkersReceivedForTesting = (): void => {
	receivedForContentId = undefined;
	subscribers.clear();
};
